import React, {useCallback, useEffect, useRef} from "react";
import { FormikErrors } from "formik";

interface ScrollToErrorProps {
    errors: FormikErrors<any>;
    submitClickedCount: number;
    refs?: Record<string, React.RefObject<HTMLElement>>;
    focusDelay?: number;
}

export const useScrollToError = ({ errors, submitClickedCount, refs = {}, focusDelay = 200 }: ScrollToErrorProps) => {
    const prevCount = useRef(submitClickedCount);

  const scrollAndFocus = useCallback(
    (element: HTMLElement) => {
      const yOffset = -100;
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.scrollY;
      const scrollToY = absoluteElementTop + yOffset;

      window.scrollTo({
        top: scrollToY,
        behavior: "smooth",
      });

      const focusable =
        (element.querySelector("input, select, textarea, [tabindex]") as HTMLElement | null) || element;

      if (focusable && typeof focusable.focus === "function") {
        setTimeout(() => {
          focusable.focus({ preventScroll: true });
        }, focusDelay);
      }
    },
    [focusDelay]
  );

    useEffect(() => {
        const hasErrors = errors && Object.keys(errors).length > 0;
        const countChanged = submitClickedCount !== prevCount.current;

        if (countChanged && hasErrors) {
            const formElements = Array.from(
                document.querySelectorAll("input[name], select[name], textarea[name], [data-error-field]")
            ) as HTMLElement[];

            const firstErrorElement = formElements.find((element) => {
                const name = element.getAttribute("name") || element.getAttribute("data-error-field");
                return name && errors[name];
            });

            const errorFields = Object.keys(errors);
            let elementToScroll = firstErrorElement;

            for (const fieldName of errorFields) {
                const refElement = refs[fieldName]?.current;
                if (refElement) {
                    elementToScroll = refElement;
                    break;
                }
            }

            if (elementToScroll) {
                setTimeout(() => {
                    scrollAndFocus(elementToScroll as HTMLElement);
                }, focusDelay);
            }
        }

        prevCount.current = submitClickedCount;
    }, [submitClickedCount, errors, refs, focusDelay, scrollAndFocus]);
};
